import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Download, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { getTaxSettings, downloadForm16 } from '../../lib/taxApi';
import { systemAPI } from '../../lib/api';
import { toast } from 'sonner';

const TaxCertificates = () => {
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [systemConfig, setSystemConfig] = useState(null);
    const [selectedYear, setSelectedYear] = useState('');
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const [data, sysResponse] = await Promise.all([
                getTaxSettings(),
                systemAPI.getSettings()
            ]);
            setSettings(data);
            setSystemConfig(sysResponse.data);
            setSelectedYear(data.current_financial_year);
        } catch (error) {
            toast.error('Failed to fetch tax settings');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await downloadForm16(selectedYear);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Form16_${selectedYear}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Form 16 downloaded successfully');
        } catch (error) {
            toast.error('Failed to download Form 16. Ensure payroll is locked for this period.');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6">
            <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold tracking-tight">Tax Certificates</h1>
                <p className="text-muted-foreground">
                    Download your annual income tax certificates and Form 16.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        <CardTitle>Form 16 (Part B)</CardTitle>
                    </div>
                    <CardDescription>
                        Certificate under section 203 of the Income-tax Act, 1961 for tax deducted at source on salary.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {systemConfig?.form16_download_enabled ? (
                        <>
                            <div className="flex items-end gap-4">
                                <div className="space-y-2 flex-1 max-w-[200px]">
                                    <label className="text-sm font-medium">Financial Year</label>
                                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Year" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="2025-26">2025-26</SelectItem>
                                            <SelectItem value="2024-25">2024-25</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    className="gap-2"
                                >
                                    {downloading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4" />
                                    )}
                                    Download PDF
                                </Button>
                            </div>

                            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg flex gap-3">
                                <AlertCircle className="h-5 w-5 text-blue-600 shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold mb-1">Important Note</p>
                                    Form 16 is typically generated after the end of the financial year and once the company has filed its Q4 TDS returns.
                                    The document generated here is a **Provisional Form 16** based on current locked payroll data.
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="p-8 text-center bg-slate-50 border border-dashed rounded-lg">
                            <ShieldCheck className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                            <h3 className="font-medium text-slate-800">Administrator Restriction</h3>
                            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                                The download of Form 16 is currently disabled by the system administrator.
                                Please contact your HR department for more information or wait until the financial year closing is complete.
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="bg-slate-50 border-dashed border-2">
                <CardContent className="p-10 flex flex-col items-center justify-center text-center space-y-3">
                    <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <ShieldCheck className="h-6 w-6 text-slate-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-900">Digital Signature Certificate</h3>
                        <p className="text-sm text-slate-500">
                            Other certificates like Form 12BA will be available here once the FY reconciliation is complete.
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default TaxCertificates;
